import { Component, effect, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ServiceCreateDialogStore } from './service-create-dialog.store';
import { BaseDialogComponent } from '../../../../../components/kit/base-dialog/base-dialog.component';
import {
    ServiceBasicInfoFormComponent,
} from '../../../components/services/service-basic-info-form/service-basic-info-form.component';
import { Validators } from '@angular/forms';
import { AddressDetailObject, ServiceCreateInput } from '@it2go/types';
import { AppFormGroup } from '../../../../../utils/form.utils';
import { fb } from '../../../../shared/shared.global-service';
import { toSignal } from '@angular/core/rxjs-interop';
import { isInvalid } from '../../../../shared/helpers/validate';
import { mapAddress } from '../../../../shared/helpers/map-address.helper';
import { ActivatedRoute, Router } from '@angular/router';
import { contentGlobalErrors, successCreated } from '../../../content.error';
import { RuianService } from '../../../../shared/components/map/ruian.service';

@Component({
    selector: 'app-service-create-dialog',
    templateUrl: './service-create-dialog.component.html',
    providers: [ServiceCreateDialogStore, RuianService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ServiceBasicInfoFormComponent,
    ],
})
export class ServiceCreateDialogComponent extends BaseDialogComponent {

    public readonly store = inject(ServiceCreateDialogStore);
    public readonly router = inject(Router);
    public readonly route = inject(ActivatedRoute);
    private readonly ruianService = inject(RuianService);

    public readonly serviceBasicInfoForm = viewChild<ServiceBasicInfoFormComponent>(ServiceBasicInfoFormComponent);

    protected form = new AppFormGroup({
        serviceTypeId: fb.control<number | null>(null, Validators.required),
        centerId: fb.control<number | null>(null),
        name: fb.control<string | null>(null, Validators.required),
        regionId: fb.control<number | null>(null, Validators.required),
        territorialVzpWorkplaceCode: fb.control<string | null>(null, Validators.required),
        registeredFrom: fb.control<Date | null>(new Date(), Validators.required),
        registeredTo: fb.control<Date | null>(null),
        ic: fb.control<string | null>(null, Validators.maxLength(8)),
        icz: fb.control<string | null>(null, Validators.maxLength(8)),
        icpe: fb.control<string | null>(null, Validators.maxLength(8)),
        form: fb.control<string[] | null>(null, Validators.required),
        address: fb.control<AddressDetailObject | null>(null, Validators.required),
        phone: fb.control<string | null>(null),
        email: fb.control<string | null>(null, Validators.email),
        userId: fb.control<string | null>(null),
        systemFrom: fb.control<Date | null>(null),
        systemTo: fb.control<Date | null>(null),
        bankAccountNumberPrefix: fb.control<string | null>(null, Validators.maxLength(6)),
        bankAccountNumber: fb.control<string | null>(null, [Validators.required, Validators.maxLength(10)]),
        bankCode: fb.control<string | null>(null, Validators.required),
        variableSymbol: fb.control<string | null>(null, Validators.maxLength(6)),

        // social
        identifier: fb.control<string | null>(null),
        regionReportingIds: fb.control<number[] | null>(null),
        sharePersons: fb.control<boolean>(true),
        regionScopeIds: fb.control<number[] | null>(null),
    });

    formRegionIdChanged = toSignal(this.form?.controls?.regionId.valueChanges);

    constructor() {
        super();

        effect(() => {
            const regionId = this.formRegionIdChanged();

            if (regionId) {
                this.form.patchValue({
                    regionReportingIds: [regionId],
                    regionScopeIds: [regionId],
                });
            }
        });
    }

    public override async save(_event: Event): Promise<void> {
        if (isInvalid(this.form)) return;

        const address = await this.appendOrp(mapAddress(this.form.controls.address.value!));
        let data: any = {
            organizationId: this.store.globalGlobalStore.organizationStore.selectedId(),
            serviceTypeId: this.form.controls.serviceTypeId.value,
            centerId: this.form.controls.centerId.value,
            name: this.form.controls.name.value,
            regionId: this.form.controls.regionId.value,
            registeredFrom: this.form.controls.registeredFrom.value!.toISOString(),
            registeredTo: this.form.controls.registeredTo.value?.toISOString() || null,
            ic: this.form.controls.ic.value || null,
            icpe: this.form.controls.icpe.value || null,
            form: this.form.controls.form.value,
            address,
            phone: this.form.controls.phone.value || null,
            email: this.form.controls.email.value || null,
            userId: this.form.controls.userId.value,
            systemFrom: this.form.controls.systemFrom.value?.toISOString() || null,
            systemTo: this.form.controls.systemTo.value?.toISOString() || null,
            bankAccountNumberPrefix: this.form.controls.bankAccountNumberPrefix.value || null,
            bankAccountNumber: this.form.controls.bankAccountNumber.value,
            bankCode: this.form.controls.bankCode.value,
            variableSymbol: this.form.controls.variableSymbol.value || null,
        };

        if (this.serviceBasicInfoForm()?.isSocial()) {
            data = {
                ...data,
                identifier: this.form.controls.identifier.value || null,
                regionReportingIds: this.form.controls.regionReportingIds.value || null,
                sharePersons: this.form.controls.sharePersons.value || null,
                regionScopeIds: this.form.controls.regionScopeIds.value || null,
            };
        }

        if (this.serviceBasicInfoForm()?.isHealthCare()) {
            data = {
                ...data,
                territorialVzpWorkplaceCode: this.form.controls.territorialVzpWorkplaceCode.value,
                icz: this.form.controls.icz.value || null,
            };
        }

        await this.store.api.createService(data as ServiceCreateInput).then((it) => {
            if (it) {
                successCreated();
                this.dialogRef.close();
                this.router.navigate([`./content/services/${it.id}`]).then();
            } else {
                contentGlobalErrors.next("database.servicesAdministrationPage.errors.save")
            }
        }).catch(err => {
            console.error(err)
            contentGlobalErrors.next("database.servicesAdministrationPage.errors.save")
        })
    }

    private async appendOrp(address: AddressDetailObject): Promise<AddressDetailObject> {
        const orp = await this.ruianService.getOrp(address.position);
        if (!orp) return address;

        return {
            ...address,
            regionalStructure: [
                ...address.regionalStructure.filter((item) => item.type !== 'regional.orp'),
                { name: orp.nazev, type: 'regional.orp', isoCode: null },
            ],
        };
    }
}
